'use client';
import { useRef, useState } from 'react';
import Image from 'next/image';
import { formatHighlightedText } from '@/lib/utils';
import SvgArrowUp from '@/components/Icons/SvgArrowUp';
import SvgArrowDown from '@/components/Icons/SvgArrowDown';
import SvgDotIndicator from '@/components/Icons/SvgDotIndicator';

function DataBlockContent({
  dataBlock,
  showDescription = true,
  animateDescription = false
}) {
  return (
    <>
      {dataBlock.data_cards?.length > 0 && (
        <div
          className={`grid grid-cols-1 gap-2 px-2 ${dataBlock.layout === '3 Cards' ? 'sm:grid-cols-3' : dataBlock.layout === '2 Cards' ? 'sm:grid-cols-2' : ''}`}
        >
          {dataBlock.data_cards.map((card) => (
            <div
              key={card._metadata?.uid}
              className={`flex flex-col gap-2 ${
                dataBlock.layout === '3 Cards' ||
                dataBlock.layout === '2 Cards' ||
                dataBlock.layout === '1 Card'
                  ? `self-stretch rounded-lg bg-white/10 sm:flex-1 ${dataBlock.layout === '3 Cards' ? 'items-center gap-2 px-5 py-3 sm:gap-3 sm:p-5' : 'items-center gap-3 p-5'}`
                  : ''
              }`}
            >
              {card.card_title && (
                <p
                  className={`${
                    dataBlock.layout === '3 Cards' ||
                    dataBlock.layout === '2 Cards' ||
                    dataBlock.layout === '1 Card'
                      ? 'preheading-s text-parlor-green-10 grow self-stretch text-center'
                      : 'font-bold tracking-widest uppercase'
                  }`}
                  style={
                    dataBlock.layout !== '3 Cards' &&
                    dataBlock.layout !== '2 Cards' &&
                    dataBlock.layout !== '1 Card'
                      ? {
                          color: dataBlock.secondary_text_color || 'white'
                        }
                      : undefined
                  }
                >
                  {card.card_title}
                </p>
              )}
              {card.large_text && (
                <p
                  className={`${
                    dataBlock.layout === '3 Cards'
                      ? 'rating-data-s lg:rating-data-l text-parlor-green-10 self-stretch text-center'
                      : 'h2 text-parlor-green-10'
                  }`}
                >
                  {card.large_text}
                </p>
              )}
              {card.image?.url && (
                <Image
                  src={card.image.url}
                  alt={card.card_caption || card.card_title || ''}
                  width={card.image.dimension?.width ?? 700}
                  height={card.image.dimension?.height ?? 400}
                  className="w-full object-cover"
                />
              )}
              {card.card_caption && (
                <p
                  className={`${
                    dataBlock.layout === '2 Cards' ||
                    dataBlock.layout === '1 Card'
                      ? 'body-r text-parlor-green-10 self-stretch text-center'
                      : 'text-white/70'
                  }`}
                >
                  {card.card_caption}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {dataBlock.description && animateDescription && (
        <div
          className={`grid transition-[grid-template-rows,margin] ${
            showDescription ? 'duration-500 ease-out' : 'duration-300 ease-in'
          } ${
            showDescription
              ? 'mt-0 grid-rows-[1fr]'
              : 'pointer-events-none -mt-5 grid-rows-[0fr] lg:-mt-8'
          }`}
        >
          <div className="overflow-hidden">
            <p className="body-r text-parlor-green-10 lg:body-l px-2">
              {formatHighlightedText(dataBlock.description, {
                color: dataBlock.secondary_text_color
              })}
            </p>
          </div>
        </div>
      )}
      {dataBlock.description && !animateDescription && showDescription && (
        <p className="body-r text-parlor-green-10 lg:body-l mt-5 px-2 lg:mt-8">
          {formatHighlightedText(dataBlock.description, {
            color: dataBlock.secondary_text_color
          })}
        </p>
      )}
    </>
  );
}

export default function LabData({ block, position }) {
  const {
    uid,
    heading,
    lab_studies,
    show_label,
    hide_label,
    footer_text,
    display_expanded_content
  } = block?.lab_data?.reference?.[0] ?? {};

  const [activeIndex, setActiveIndex] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const sectionRef = useRef(null);
  const areDetailsExpanded = display_expanded_content || showDetails;

  const studies = lab_studies ?? [];
  const activeStudy = studies[activeIndex];
  const reviewCards = activeStudy?.review_card ?? [];
  const handleToggleDetails = () => {
    if (display_expanded_content) return;

    if (showDetails) {
      setShowDetails(false);
      window.setTimeout(() => {
        if (!sectionRef.current) return;
        const topOffset = 96;
        const targetTop =
          sectionRef.current.getBoundingClientRect().top +
          window.scrollY -
          topOffset;

        window.scrollTo({
          top: Math.max(0, targetTop),
          behavior: 'smooth'
        });
      }, 300);
      return;
    }
    setShowDetails(true);
  };

  return (
    <section
      ref={sectionRef}
      id={`${uid}_${position}`}
      data-name="lab-data"
      className="flex w-full flex-col items-center justify-center gap-15 px-5 py-20 lg:gap-20 lg:px-10 lg:pb-30 xl:px-30"
      style={{
        background:
          'radial-gradient(62.24% 67.2% at 89.1% 24.68%, #AD7D6C 0%, rgba(173, 125, 108, 0.00) 99.13%), radial-gradient(83.12% 59% at 3.13% 22%, #AD7D6C 0%, rgba(173, 125, 108, 0.00) 99.13%), radial-gradient(82.7% 59.88% at 50% 46.89%, #AD7D6C 0%, rgba(173, 125, 108, 0.00) 99.13%), #774432'
      }}
    >
      {heading && (
        <h2 className="h3 lg:h2 self-stretch text-center text-white">
          {heading}
        </h2>
      )}

      <div className="max-w-container flex w-full flex-col items-start gap-4 lg:gap-10">
        {/* Tab buttons */}
        <div className="flex flex-wrap gap-3">
          {studies.map((study, index) => {
            const isActive = activeIndex === index;
            return (
              <button
                key={study._metadata?.uid ?? index}
                onClick={() => {
                  setActiveIndex(index);
                }}
                className={`group flex items-center gap-3 rounded-lg px-3 py-2 transition-colors lg:px-4 lg:py-3 ${
                  isActive ? 'bg-white/80' : 'bg-black/20 hover:bg-black/40'
                }`}
              >
                {study.tab_title && (
                  <span
                    className={`preheading-s text-${isActive ? 'parlor-green-80' : 'parlor-green-20'} text-center ${
                      isActive ? '' : 'group-hover:text-parlor-green-10'
                    }`}
                  >
                    {study.tab_title}
                  </span>
                )}
                {study.tab_score != null && (
                  <span
                    className={`preheading-s text-light-green rounded px-1 py-0.5 ${
                      isActive
                        ? 'bg-black/60'
                        : 'bg-black/20 group-hover:bg-black/40'
                    }`}
                  >
                    {study.tab_score}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active tab content */}
        {activeStudy && (
          <div className="flex w-full flex-col items-start gap-5 lg:flex-row lg:items-start">
            {/* Main content panel */}
            <div className="flex w-full flex-col items-start gap-5 rounded-3xl bg-black/15 p-4 sm:gap-5 lg:flex-[1_0_0] lg:gap-8 lg:rounded-[20px] lg:p-6">
              {/* Result header */}
              {(activeStudy.test_result_score != null ||
                activeStudy.test_result_title) && (
                <div className="flex w-full items-start justify-between self-stretch">
                  {/* Score + title */}
                  <div className="flex flex-col items-start sm:gap-2 lg:gap-3">
                    {activeStudy.test_result_score != null && (
                      <div className="flex items-baseline gap-2">
                        <span className="text-light-green rating-l lg:rating-xl">
                          {activeStudy.test_result_score}
                        </span>
                        {activeStudy.test_result_score && (
                          <span className="rating-s lg:rating-m text-parlor-green-20 inline-flex items-baseline gap-2">
                            <span>/</span>
                            <span>10</span>
                          </span>
                        )}
                      </div>
                    )}
                    {activeStudy.test_result_title && (
                      <span className="h5 lg:h4 px-2 text-white">
                        {activeStudy.test_result_title}
                      </span>
                    )}
                  </div>

                  {/* Icon */}
                  {activeStudy.test_icon?.url && (
                    <div className="flex h-10 w-10 items-center justify-center gap-2.5 rounded-[40px] bg-white/10 lg:h-16 lg:w-16">
                      <Image
                        src={activeStudy.test_icon.url}
                        alt={activeStudy.test_icon.title ?? ''}
                        width={32}
                        height={32}
                        className="h-6 w-6 lg:h-8 lg:w-8"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Data blocks */}
              {activeStudy.data_blocks?.map((dataBlock, blockIndex) => {
                const isDetailsBlock = blockIndex > 0;

                if (isDetailsBlock) {
                  return (
                    <div
                      key={dataBlock._metadata?.uid}
                      className={`grid transition-[grid-template-rows,margin] lg:gap-8 ${
                        areDetailsExpanded
                          ? 'duration-500 ease-out'
                          : 'duration-300 ease-in'
                      } ${
                        areDetailsExpanded
                          ? 'grid-rows-[1fr]'
                          : 'pointer-events-none -mt-5 grid-rows-[0fr] lg:-mt-8'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <DataBlockContent dataBlock={dataBlock} />
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={dataBlock._metadata?.uid}
                    className="flex w-full flex-col gap-5 lg:gap-8"
                  >
                    <DataBlockContent
                      dataBlock={dataBlock}
                      showDescription={areDetailsExpanded}
                      animateDescription
                    />
                  </div>
                );
              })}

              <div className="flex w-full flex-col gap-5 self-stretch">
                {/* Footer text — mobile: above hr */}
                {footer_text && showDetails && (
                  <div className="body-r text-dawn-grey-60 underline-offset-5 sm:hidden [&_div]:m-0 [&_div]:inline [&_p]:m-0 [&_p]:inline">
                    <span className="inline-flex items-center px-2 align-middle">
                      <SvgDotIndicator />
                    </span>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: footer_text
                      }}
                    />
                  </div>
                )}

                <hr className="bg-dawn-grey h-px w-full border-none" />

                <div
                  className={`flex w-full flex-col items-start gap-5 sm:items-center ${
                    footer_text
                      ? 'sm:flex-row-reverse sm:justify-between'
                      : 'sm:flex-row sm:justify-start'
                  }`}
                >
                  {/* Footer text — mobile: below hr when expanded */}
                  {footer_text && !showDetails && (
                    <div className="body-r text-dawn-grey-60 underline-offset-5 sm:hidden [&_div]:m-0 [&_div]:inline [&_p]:m-0 [&_p]:inline">
                      <span className="inline-flex items-center px-2 align-middle">
                        <SvgDotIndicator />
                      </span>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: footer_text
                        }}
                      />
                    </div>
                  )}

                  {/* Footer text — desktop: in the row */}
                  {footer_text && (
                    <div className="body-r text-dawn-grey-60 hidden px-2 underline-offset-5 sm:block sm:[&_div]:m-0 sm:[&_div]:inline sm:[&_p]:m-0 sm:[&_p]:inline">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: footer_text
                        }}
                      />
                      <span className="ml-2 inline-flex items-center align-middle whitespace-nowrap">
                        <SvgDotIndicator />
                      </span>
                    </div>
                  )}

                  {/* Show/hide test details toggle */}
                  {!display_expanded_content && (show_label || hide_label) && (
                    <button
                      onClick={handleToggleDetails}
                      className="preheading-s text-dawn-grey-20 hover:text-light-green w-full px-2 text-center transition-colors sm:w-auto sm:text-left"
                    >
                      {areDetailsExpanded
                        ? (hide_label ?? 'Hide Test Details')
                        : (show_label ?? 'Show Test Details')}
                      <span className="ml-2 inline-block align-text-bottom">
                        {areDetailsExpanded ? (
                          <SvgArrowUp stroke="currentColor" />
                        ) : (
                          <SvgArrowDown stroke="currentColor" />
                        )}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Review cards */}
            {reviewCards.length > 0 && (
              <div className="flex w-full flex-col gap-5 lg:w-[36%] lg:max-w-118 lg:shrink lg:grow-0 xl:w-118 xl:shrink-0">
                {reviewCards.map((reviewCard, index) => (
                  <div
                    key={reviewCard._metadata?.uid ?? index}
                    className="flex flex-col items-start gap-5 rounded-[20px] bg-black/15 p-6 lg:p-8"
                  >
                    {reviewCard.star_rating != null && (
                      <div className="flex gap-1">
                        {Array.from({ length: reviewCard.star_rating }).map(
                          (_, i) => (
                            <span key={i} className="text-parlor-green-20">
                              ★
                            </span>
                          )
                        )}
                      </div>
                    )}
                    {reviewCard.review_quote && (
                      <p className="h6 lg:h5 text-parlor-green-10">
                        {formatHighlightedText(reviewCard.review_quote, {
                          color: reviewCard.secondary_text_color
                        })}
                      </p>
                    )}
                    <div className="flex flex-col gap-1">
                      {reviewCard.reviewer_name && (
                        <span className="sh12 sm:sh11 text-parlor-green-20">
                          {reviewCard.reviewer_name}
                        </span>
                      )}
                      {reviewCard.product_name && (
                        <span className="body-xs sm:body-s text-parlor-green-20">
                          {reviewCard.product_name}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
